import { useEffect, useState, type SyntheticEvent } from 'react';
import {
  durationMinutes,
  formatScheduleLocal,
  scheduleTimePatch,
  type ScheduleTimes,
} from './schedule-time.js';
import './ShootTimeFields.css';
import './crm-form-fields.css';

export function CompactShootSchedule({
  scheduledStartAt,
  scheduledEndAt,
  defaultDurationMinutes = 60,
  onChange,
  onValidityChange,
  showTimezone = true,
}: ScheduleTimes & {
  defaultDurationMinutes?: number;
  showTimezone?: boolean;
  onChange(patch: Partial<ScheduleTimes>): void;
  onValidityChange(valid: boolean): void;
}) {
  const initialMinutes =
    Number(durationMinutes({ scheduledStartAt, scheduledEndAt })) || defaultDurationMinutes;
  const [date, setDate] = useState(() => formatScheduleLocal(scheduledStartAt).slice(0, 10));
  const [time, setTime] = useState(() => formatScheduleLocal(scheduledStartAt).slice(11, 16));
  const [minutes, setMinutes] = useState(initialMinutes);
  const [custom, setCustom] = useState(![30, 60, 120].includes(initialMinutes));
  const [hours, setHours] = useState(String(initialMinutes / 60));
  const [error, setError] = useState('');
  const [chosenDuration, setChosenDuration] = useState(false);
  const times = { scheduledStartAt, scheduledEndAt };
  useEffect(() => {
    setDate(formatScheduleLocal(scheduledStartAt).slice(0, 10));
    setTime(formatScheduleLocal(scheduledStartAt).slice(11, 16));
  }, [scheduledStartAt]);
  useEffect(() => {
    if (!chosenDuration && !scheduledEndAt) {
      setMinutes(defaultDurationMinutes);
      setHours(String(defaultDurationMinutes / 60));
      setCustom(![30, 60, 120].includes(defaultDurationMinutes));
    }
  }, [defaultDurationMinutes, chosenDuration, scheduledEndAt]);
  const [badDate, setBadDate] = useState(false);
  const [badTime, setBadTime] = useState(false);
  useEffect(() => {
    onValidityChange(!error);
  }, [error, onValidityChange]);
  const selectedMinutes = custom ? (hours.trim() ? Number(hours) * 60 : NaN) : minutes;
  const commit = (
    nextDate: string,
    nextTime: string,
    value: number,
    invalidDate = badDate,
    invalidTime = badTime,
  ) => {
    const durationError = !Number.isFinite(value) || value < 1 || value > 10080;
    if (invalidDate || invalidTime || Boolean(nextDate) !== Boolean(nextTime)) {
      setError('Choose a date and start time.');
      onValidityChange(false);
      return;
    }
    if (durationError) {
      setError('Enter a duration between 1 minute and 168 hours.');
      onValidityChange(false);
      return;
    }
    let nextStart: string | null = null;
    if (nextDate && nextTime) {
      const raw = `${nextDate}T${nextTime}`;
      if (formatScheduleLocal(scheduledStartAt).slice(0, 16) === raw) nextStart = scheduledStartAt;
      else {
        const result = scheduleTimePatch(times, 'start', raw);
        if (result.error) {
          setError(result.error);
          onValidityChange(false);
          return;
        }
        nextStart = result.patch?.scheduledStartAt ?? null;
      }
    }
    const same = (a: string | null, b: string | null) =>
      a === b || (a !== null && b !== null && Date.parse(a) === Date.parse(b));
    // Merely leaving an unchanged field must not materialize a default end.
    // On mobile that draft update also shifts the sheet during a pointer click.
    if (
      same(nextStart, scheduledStartAt) &&
      !scheduledEndAt &&
      !chosenDuration &&
      value === initialMinutes
    ) {
      setError('');
      onValidityChange(true);
      return;
    }
    const result = nextStart
      ? scheduleTimePatch(
          { scheduledStartAt: nextStart, scheduledEndAt: null },
          'duration',
          String(value),
        )
      : { patch: { scheduledEndAt: null } };
    if (result.error) {
      setError(result.error);
      onValidityChange(false);
      return;
    }
    setError('');
    onValidityChange(true);
    const nextEnd = result.patch?.scheduledEndAt ?? null;
    // No-op display must retain sub-minute precision of existing instants.
    if (
      same(nextStart, scheduledStartAt) &&
      Math.abs(value - Number(durationMinutes(times))) < 0.000001
    )
      return;
    if (!same(nextStart, scheduledStartAt) || !same(nextEnd, scheduledEndAt))
      onChange({ scheduledStartAt: nextStart, scheduledEndAt: nextEnd });
  };
  const dateInput = (event: SyntheticEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const bad = event.currentTarget.validity.badInput;
    setDate(value);
    setBadDate(bad);
    commit(value, time, selectedMinutes, bad, badTime);
  };
  const timeInput = (event: SyntheticEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const bad = event.currentTarget.validity.badInput;
    setTime(value);
    setBadTime(bad);
    commit(date, value, selectedMinutes, badDate, bad);
  };
  return (
    <div className="shoot-time-fields shoot-schedule-compact">
      <div className="shoot-schedule-compact__start crm-form-pair">
        <label className="field">
          <span>Date</span>
          <input
            aria-label="Date"
            type="date"
            min="0001-01-01"
            max="9999-12-31"
            value={date}
            aria-invalid={Boolean(error)}
            onInput={dateInput}
            onChange={dateInput}
            onKeyUp={dateInput}
            onBlur={dateInput}
          />
        </label>
        <label className="field">
          <span>Start time</span>
          <input
            aria-label="Start time"
            type="time"
            step="60"
            value={time}
            aria-invalid={Boolean(error)}
            onInput={timeInput}
            onChange={timeInput}
            onKeyUp={timeInput}
            onBlur={timeInput}
          />
        </label>
      </div>
      <fieldset className="shoot-schedule-compact__duration">
        <legend>Duration</legend>
        <div className="shoot-schedule-compact__choices">
          {[
            [30, '30 min'],
            [60, '1 hour'],
            [120, '2 hours'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className="shoot-button shoot-button--outline"
              aria-pressed={!custom && minutes === value}
              onClick={() => {
                setCustom(false);
                setHours(String(Number(value) / 60));
                setMinutes(Number(value));
                setChosenDuration(true);
                commit(date, time, Number(value));
              }}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            className="shoot-button shoot-button--outline"
            aria-pressed={custom}
            onClick={() => {
              setCustom(true);
              setChosenDuration(true);
            }}
          >
            Custom
          </button>
        </div>
        {custom ? (
          <label className="field">
            <span>Hours</span>
            <input
              aria-label="Hours"
              type="number"
              inputMode="decimal"
              step="any"
              min="0.016667"
              max="168"
              value={hours}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setHours(value);
                setChosenDuration(true);
                commit(date, time, value.trim() ? Number(value) * 60 : NaN);
              }}
            />
          </label>
        ) : null}
      </fieldset>
      {error ? (
        <p className="shoot-time-fields__error" role="alert">
          {error}
        </p>
      ) : null}
      {showTimezone ? (
        <p className="shoot-panel__hint">
          Time in {Intl.DateTimeFormat().resolvedOptions().timeZone}
        </p>
      ) : null}
    </div>
  );
}
